/**
 * Devices a family has linked, and taking one back.
 *
 * A linked device holds a bearer token. It is the only credential in this
 * system that is not a person signing in, which makes it the one a parent most
 * needs to be able to see and revoke: a tablet handed on, a display sold, a
 * phone left at a friend's house.
 *
 * The label is ciphertext for the usual reason -- parents call them "Ravi's
 * tablet", which is a child's name in a field.
 */

import { api } from '../api/client';
import { decryptField, encryptField, packWrapped, wrapFamilyKey } from '../crypto/familyKey';
import { normalise } from './codes';
import { importPublicKey, wrapForDevice } from '../crypto/deviceKey';
import type { Person } from './state';

export type Scope = 'display' | 'child';
export type DeviceStatus = 'pending' | 'approved' | 'denied' | 'discarded';

export interface Device {
  id: string;
  scope: Scope;
  personId: string | null;
  /** Decrypted here. */
  label: string;
  /** `pending` until a parent says yes. Absent on the wire means approved. */
  status: DeviceStatus;
  /** `phone` / `tablet` / `computer` / `other`, or null for a display. */
  kind: string | null;
  /**
   * The emoji-and-four-characters a parent checks against the child's screen.
   * Recomputed by the server from the device's public key and stored nowhere,
   * so there is one derivation of it and no second copy to drift.
   */
  match: string | null;
  /**
   * The device's PUBLIC key, which the approving parent wraps the person key
   * to. Not a secret: it encrypts to that device and decrypts nothing. That
   * is the difference between it and the token hash, which never leaves the
   * server at all.
   */
  devicePub: string | null;
  created: string;
  approvedAt: string | null;
  deniedAt: string | null;
  /** Set aside because another device's approval spent the same code. */
  discardedAt: string | null;
  lastSeenDate: string | null;
  revoked: boolean;
}

interface RawDevice {
  id: string;
  scope: Scope;
  personId?: string | null;
  labelCt?: string | null;
  status?: DeviceStatus;
  kind?: string | null;
  match?: string | null;
  devicePub?: string | null;
  created?: string;
  approvedAt?: string | null;
  deniedAt?: string | null;
  discardedAt?: string | null;
  lastSeenDate?: string | null;
  revoked?: boolean;
}

export async function listDevices(familyId: string, key: CryptoKey): Promise<Device[]> {
  const result = await api.get<{ devices: RawDevice[] }>(`/v1/families/${familyId}/devices`);
  return Promise.all(
    result.devices.map(async (device) => ({
      id: device.id,
      scope: device.scope,
      personId: device.personId ?? null,
      label: device.labelCt ? await open(key, device.labelCt) : '',
      // Absent means approved: every device linked before approval existed
      // is one, and the server says the same. See handlers/authorizer.py.
      status: device.status ?? 'approved',
      kind: device.kind ?? null,
      match: device.match ?? null,
      devicePub: device.devicePub ?? null,
      created: device.created ?? '',
      approvedAt: device.approvedAt ?? null,
      deniedAt: device.deniedAt ?? null,
      discardedAt: device.discardedAt ?? null,
      lastSeenDate: device.lastSeenDate ?? null,
      revoked: Boolean(device.revoked),
    })),
  );
}

async function open(key: CryptoKey, sealed: string): Promise<string> {
  try {
    return await decryptField(key, sealed);
  } catch {
    return '';
  }
}

/**
 * A code for a new WALL DISPLAY.
 *
 * Two calls, because this device cannot wrap anything until the server has
 * chosen the code. In between, the code is not redeemable -- so nobody can
 * link a display in the moment before it can read anything.
 *
 * A display gets the FAMILY key, because the wall shows every name. That is
 * also why its code is twenty characters: the wrapping sits in the table
 * where a dump could attack it offline.
 *
 * THERE IS NO CHILD BRANCH HERE ANY MORE. A child's device is not something
 * a parent mints a code for at the moment the child is standing next to
 * them -- that was the whole problem. The child has a durable code of their
 * own (`mintChildCode` below), types it whenever they need to, and a parent
 * approves the request that produces. The key is handed over at approval, by
 * `approveDevice`, wrapped to that device's own public key.
 */
export async function linkDisplay(
  familyId: string,
  key: CryptoKey,
  label: string,
): Promise<{ code: string; expiresInSeconds: number }> {
  const minted = await api.write<{ code: string; expiresInSeconds: number }>(
    'POST',
    `/v1/families/${familyId}/link-codes`,
    {
      scope: 'display',
      labelCt: await encryptField(key, label),
      // Says "hold this code back until I send the key", so nobody can redeem
      // one in the moment before it can read anything.
      expectsKey: true,
    },
  );

  const code = normalise(minted.code, 20);
  await api.write('PUT', `/v1/families/${familyId}/link-codes`, {
    code,
    keyBundle: packWrapped(await wrapFamilyKey(key, code)),
  });

  return minted;
}

/**
 * A child's own code. Eight characters, durable, and it carries nothing.
 *
 * Two calls, and the second is not about a key -- there is none to send. The
 * server chooses the code so the entropy is honestly server-side, and only a
 * device holding the family key can seal it into `codeCt` so that Settings
 * can show it back later. Without that second call a parent would get exactly
 * one look at it, which is the problem the family code already solved once.
 */
export async function mintChildCode(
  familyId: string,
  key: CryptoKey,
  personId: string,
): Promise<string> {
  const minted = await api.write<{ code: string }>(
    'POST',
    `/v1/families/${familyId}/people/${personId}/code`,
    {},
  );
  const code = normalise(minted.code, 8);
  await api.write('PUT', `/v1/families/${familyId}/people/${personId}/code`, {
    code,
    codeCt: await encryptField(key, code),
  });
  return code;
}

/** The current code for one child, read back from `codeCt` on their row. */
export async function readChildCode(
  person: Person,
  key: CryptoKey,
): Promise<string | null> {
  if (!person.codeCt) return null;
  try {
    return normalise(await decryptField(key, person.codeCt), 8);
  } catch {
    return null;
  }
}

/**
 * Say yes to a waiting device, and hand it the key.
 *
 * THIS is where a child's device gets what it needs to read its own name,
 * and it is the reason the code can carry nothing. This parent's device holds
 * the family key, so it can open `pfw` and get the person key; the child's
 * device published a public key with its request; so one is wrapped to the
 * other here and sent as bytes the server cannot read.
 *
 * The caller has already shown the parent the match code and the parent has
 * compared it with the child's screen. That comparison is the authentication
 * step, and no amount of code here can do it for them.
 */
export async function approveDevice(
  familyId: string,
  familyKey: CryptoKey,
  device: Device,
  person: Person,
  label: string,
): Promise<void> {
  if (!person.key) {
    // Somebody added before per-person keys existed. Saving their name once
    // mints one -- see `savePerson` -- and Settings does that for a parent
    // when it opens this section, so this is the message of last resort.
    throw new Error(
      'This person has no key yet. Open their name in Settings once and save ' +
        'it, then approve this again.',
    );
  }
  if (!device.devicePub) {
    throw new Error('That request did not bring a key to lock to. Ask them to '
      + 'type their code again.');
  }

  await api.write('POST', `/v1/families/${familyId}/devices/${device.id}/approve`, {
    labelCt: await encryptField(familyKey, label),
    keyWrap: await wrapForDevice(person.key, await importPublicKey(device.devicePub)),
  });

  // The code is NOT spent by this: a child's code lasts until a parent makes
  // a new one. See `approve_device` on the server for why it changed back.
}

export async function denyDevice(familyId: string, deviceId: string): Promise<void> {
  await api.write('POST', `/v1/families/${familyId}/devices/${deviceId}/deny`);
}

export async function revokeDevice(familyId: string, deviceId: string): Promise<void> {
  await api.write('DELETE', `/v1/families/${familyId}/devices/${deviceId}`);
}

export interface DeletionPlan {
  /** Families that carry on without this parent. */
  leaves: string[];
  /** Families this parent is the last of, which deleting would destroy. */
  destroys: string[];
}

export async function deletionPlan(): Promise<DeletionPlan> {
  return api.get<DeletionPlan>('/v1/me/deletion-plan');
}

/**
 * Delete this account.
 *
 * `destroy` names the families the parent has agreed to lose. The server does
 * not take that on trust -- it checks the caller really is the last parent of
 * each one -- but the typed confirmation has to happen in the app, because a
 * parent confirms by typing their family's NAME and the server has never been
 * able to read a family name.
 */
export async function deleteAccount(destroy: string[]): Promise<void> {
  await api.write('DELETE', '/v1/me', { deleteFamilies: destroy });
}
